// Справка „Натовареност" на резервациите (Справки → reservations-occupancy).
//
// СЪЩИЯТ скелет като „Продажби по периоди": период с час, два сегмента
// (Групирано / Във времето → Часове / Дни от седмицата / Месеци / Дати /
// Години), чипове-филтри (статус, маса), кутийки, графика „един стълб = един
// ред" и клиентска таблица. Разликата е какво се брои — резервации и гости,
// не сметки и евро. Режимът и единицата се помнят в localStorage със свой
// ключ (`useSalesByPeriodsView`), както там.
//
// Денят е КАЛЕНДАРЕН, не работен: резервацията за 00:30 е в деня на своята
// дата (както в модула), а работният ден би я пратил в предния.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useReservationsOccupancyReport } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import type {
  ReservationOccupancyFilters as ApiFilters,
  ReservationReportStatus,
} from "../../../api/reportsReservations";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { useSalesByPeriodsView } from "./salesByPeriods/useSalesByPeriodsView";
import { ReservationsOccupancyFilters } from "./reservations/ReservationsOccupancyFilters";
import {
  EMPTY_OCCUPANCY_FILTERS,
  type OccupancyFilterState,
} from "./reservations/occupancyFilterState";
import { OccupancyKpis } from "./reservations/OccupancyKpis";
import { OccupancyChart } from "./reservations/OccupancyChart";
import { useOccupancyColumns } from "./reservations/occupancyColumns";
import { exportOccupancy } from "./reservations/occupancyExport";
import {
  buildOccupancyRows,
  totalsOfOccupancyRows,
  type OccupancyPeriodRow,
} from "./reservations/occupancyMath";
import { useReservationPeriodFilters } from "./reservations/useReservationPeriodFilters";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

const VIEW_STORAGE_KEY = "reports.reservationsOccupancy.view.v1";

const toIds = (values: string[]): number[] =>
  values.map(Number).filter((value) => Number.isFinite(value));

export const ReservationsOccupancyReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  // Периодът е с час, както в „Продажби по периоди" — „между 18:00 и 23:00".
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const { mode, unit, changeMode, changeUnit } = useSalesByPeriodsView(VIEW_STORAGE_KEY);
  const [reportFilters, setReportFilters] =
    useState<OccupancyFilterState>(EMPTY_OCCUPANCY_FILTERS);
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<OccupancyPeriodRow[]>([]);

  const periodFilters = useReservationPeriodFilters(dateRange);
  const filters = useMemo<ApiFilters>(
    () => ({
      ...periodFilters,
      mode,
      unit,
      statuses: reportFilters.statuses as ReservationReportStatus[],
      table_ids: toIds(reportFilters.tableIds),
    }),
    [periodFilters, mode, unit, reportFilters],
  );

  const { data: report, isLoading, isFetching, refetch } =
    useReservationsOccupancyReport(filters);

  const rows = useMemo(
    () =>
      report
        ? buildOccupancyRows(report.rows, report.mode, report.unit, i18n.language)
        : [],
    [report, i18n.language],
  );

  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        row.label.toLowerCase().includes(needle) ||
        (row.sublabel ?? "").toLowerCase().includes(needle),
    );
  }, [rows, searchText]);

  // Кутийките и графиката следват ТЪРСАЧКАТА, както таблицата и Excel.
  const totals = useMemo(() => totalsOfOccupancyRows(searchedRows), [searchedRows]);
  const columns = useOccupancyColumns();

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.salesPeriodsTableKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.salesPeriodsRowOne",
          "reportsPage.salesPeriodsRowMany",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.rvJournalHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.rvOccupancy")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
          </div>

          <ReservationsOccupancyFilters
            mode={mode}
            unit={unit}
            onModeChange={changeMode}
            onUnitChange={changeUnit}
            filters={reportFilters}
            onFiltersChange={setReportFilters}
          />

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <OccupancyKpis totals={totals} unit={unit} isNarrow={narrow} />

              <OccupancyChart
                rows={searchedRows}
                total={totals.reservations}
                isNarrow={narrow}
              />

              <DataTable<OccupancyPeriodRow>
                tableId="reservations-occupancy"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => row.key}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.rvOccupancy")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.salesPeriodsSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportOccupancy({ rows: visibleRows, t })}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                initialSorting={[{ id: "period", desc: false }]}
                pageSize={50}
                emptyTitle={t("reportsPage.rvNoData")}
                emptyHint={t("reportsPage.rvEmptyHint")}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
